import { BaseEntity, MemoizeGetters } from "./base";
import { Photo } from "./photos";

interface Inspector {
  firstName: string;
  lastName: string;
}

// --- Structured (detail) inspection report ---------------------------------
// Returned by GET /orders/:uid only. The partner API resolves option IDs to
// labels, attaches photos, and organizes answers into sections → groups →
// fields. Keys arrive snake_case and are camelized by BaseEntity.

/** A photo attached to a single inspection field. */
export interface InspectionFieldPhoto {
  uid: string;
  label: string;
  url: string;
  takenAt: string;
  createdAt: string;
}

/**
 * Display-ready field value. Always render this — never `rawValue`.
 * Single-select/dropdown resolve to a string, multi-select to an array of
 * labels, photo fields carry their photos here too (we render from `photos`).
 */
export type InspectionFieldValue = string | number | boolean | string[] | null;

export interface InspectionField {
  id: string;
  key: string;
  label: string;
  /** Group label, provided for context. */
  question?: string;
  hint?: string;
  /** text | single-select | dropdown | multi-select | photo | ... */
  fieldType: string;
  required?: boolean;
  /** Display value — already label-resolved. */
  value: InspectionFieldValue;
  /** Present only when it differs from `value`. Debug-only; do not render. */
  rawValue?: string;
  photos?: InspectionFieldPhoto[];
}

export interface InspectionGroup {
  /** Absent for ungrouped fields. */
  id?: string;
  label?: string;
  fields: InspectionField[];
}

export interface InspectionSection {
  /** Absent for the root section. */
  id?: string;
  label?: string;
  groups: InspectionGroup[];
}

interface StructuredInspectionResultDetails {
  inspectionResult: string; // "Pass" | "Fail"
  inspectionDate: string;
  sections: InspectionSection[];
}

// --- Legacy (flat) inspection report ---------------------------------------
// Returned when the inspection form definition isn't available. We fall back
// to question/answer rendering for this shape.

interface LegacyInspectionResultDetails {
  inspectionResult: string;
  inspectionDate: string;
  inspectionType: string;
  inspectionDetails: {
    question: string;
    answer: string;
  }[];
}

type InspectionResultDetails =
  | StructuredInspectionResultDetails
  | LegacyInspectionResultDetails;

interface LatestInspectionDocument {
  inspectionPdfUrl: string;
}

@MemoizeGetters
export class Inspection extends BaseEntity {
  inspector: Inspector;
  /** Pass/fail boolean. Present on both list (slim) and detail responses. */
  inspectionResult?: boolean;
  inspectionPhotos: Photo[];
  inspectionResultDetails?: InspectionResultDetails;
  latestInspectionDocument: LatestInspectionDocument;

  get inspectorName() {
    if (!this.inspector) return '';
    return `${this.inspector.firstName} ${this.inspector.lastName}`.trim();
  }

  /** True when the detail endpoint returned the structured sections shape. */
  get isStructured(): boolean {
    return Array.isArray((this.inspectionResultDetails as StructuredInspectionResultDetails)?.sections);
  }

  /** Structured sections, or an empty list for slim/legacy responses. */
  get sections(): InspectionSection[] {
    return this.isStructured
      ? (this.inspectionResultDetails as StructuredInspectionResultDetails).sections
      : [];
  }

  /**
   * "Pass"/"Fail" status. Prefers the detail string; falls back to the slim
   * boolean so list views can render a badge.
   */
  get result(): string | undefined {
    const detailResult = this.inspectionResultDetails?.inspectionResult;
    if (detailResult != null) return detailResult;
    if (this.inspectionResult == null) return undefined;
    return this.inspectionResult ? 'Pass' : 'Fail';
  }

  get date() {
    return this.inspectionResultDetails?.inspectionDate;
  }

  get type() {
    return (this.inspectionResultDetails as LegacyInspectionResultDetails)?.inspectionType;
  }

  /** Legacy flat question/answer pairs. Null for structured/slim responses. */
  get details() {
    const legacy = this.inspectionResultDetails as LegacyInspectionResultDetails;
    if (!legacy?.inspectionDetails) return null;
    return legacy.inspectionDetails
      .filter(detail => detail.answer !== null)
      .map(detail => ({
        // Replace underscores with spaces and capitalize first word
        question: detail.question.replace(/_/g, ' ').replace(/^./, char => char.toUpperCase()),
        answer: detail.answer
      }));
  }

  get documentUrl() {
    return this.latestInspectionDocument?.inspectionPdfUrl;
  }

  get photos() {
    return this.inspectionPhotos;
  }
}
